/**
 * TreeLayoutService
 *
 * Service for calculating D3-based tree layouts with multiple algorithms
 * Converts TreeNode hierarchy into D3 positioned nodes for visualization
 *
 * Supports:
 * - Standard tree layout (descendant/ancestor)
 * - Radial/fan chart layout
 * - Cluster layout
 * - Performance optimization for large trees (500+ nodes)
 */

import * as d3 from 'd3';
import {
  TreeNode,
  D3TreeNode,
  D3TreeLink,
  TreeLayoutConfig,
  TreeRenderData,
  SVGDimensions,
  TreeLayoutAlgorithm,
  TreeViewMode,
  FanChartConfig,
  NodePosition,
  LinkPath,
  DEFAULT_TREE_CONFIG,
  Person,
  TreeSearchResult,
  LineagePathResult,
} from '../types';

export class TreeLayoutService {
  /**
   * Calculate tree layout using specified algorithm
   *
   * @param root - Root tree node
   * @param config - Layout configuration
   * @param algorithm - Layout algorithm to use
   * @returns Complete render data with positioned nodes and links
   */
  static calculateLayout(
    root: TreeNode,
    config: TreeLayoutConfig = DEFAULT_TREE_CONFIG,
    algorithm: TreeLayoutAlgorithm = 'tree'
  ): TreeRenderData {
    try {
      // Convert TreeNode to D3 hierarchy
      const hierarchyRoot = this.convertToD3Hierarchy(root);

      // Calculate positions based on algorithm
      let positionedRoot: D3TreeNode;
      switch (algorithm) {
        case 'tree':
          positionedRoot = this.calculateTreeLayout(hierarchyRoot, config);
          break;
        case 'cluster':
          positionedRoot = this.calculateClusterLayout(hierarchyRoot, config);
          break;
        case 'radial':
          positionedRoot = this.calculateRadialLayout(hierarchyRoot, config);
          break;
        default:
          positionedRoot = this.calculateTreeLayout(hierarchyRoot, config);
      }

      // Extract nodes and links
      const nodes = this.extractNodes(positionedRoot);
      const links = this.extractLinks(positionedRoot, nodes);

      // Calculate SVG dimensions
      const dimensions = this.calculateDimensions(nodes, config);

      return {
        nodes,
        links,
        dimensions,
        root: positionedRoot,
        visibleNodeCount: nodes.filter(n => n.isVisible !== false).length,
        linkCount: links.length,
      };
    } catch (error: any) {
      throw new Error(`Failed to calculate tree layout: ${error.message}`);
    }
  }

  /**
   * Calculate descendant tree layout (top-down or left-right)
   */
  static calculateDescendantLayout(
    root: TreeNode,
    config: TreeLayoutConfig = DEFAULT_TREE_CONFIG
  ): TreeRenderData {
    return this.calculateLayout(root, config, 'tree');
  }

  /**
   * Calculate ancestor tree layout (bottom-up or right-left)
   */
  static calculateAncestorLayout(
    root: TreeNode,
    config: TreeLayoutConfig = DEFAULT_TREE_CONFIG
  ): TreeRenderData {
    // Ancestor trees use same algorithm but may need position adjustment
    const renderData = this.calculateLayout(root, config, 'tree');

    // For ancestor view, we may want to reverse the orientation
    // This is handled by the component's transform
    return renderData;
  }

  /**
   * Calculate fan/radial chart layout
   */
  static calculateFanChartLayout(
    root: TreeNode,
    config: TreeLayoutConfig = DEFAULT_TREE_CONFIG,
    fanConfig?: Partial<FanChartConfig>
  ): TreeRenderData {
    const fullFanConfig: FanChartConfig = {
      innerRadius: 50,
      outerRadius: Math.min(config.width, config.height) / 2 - 50,
      startAngle: -Math.PI / 2,
      endAngle: Math.PI / 2,
      generationSpacing: 80,
      nodeSpacing: 10,
      ...fanConfig,
    };

    const hierarchyRoot = this.convertToD3Hierarchy(root);
    const positionedRoot = this.calculateRadialLayoutWithConfig(hierarchyRoot, config, fullFanConfig);

    const nodes = this.extractNodes(positionedRoot);
    const links = this.extractLinks(positionedRoot, nodes);
    const dimensions = this.calculateDimensions(nodes, config);

    return {
      nodes,
      links,
      dimensions,
      root: positionedRoot,
      visibleNodeCount: nodes.filter(n => n.isVisible !== false).length,
      linkCount: links.length,
    };
  }

  /**
   * Calculate hourglass layout (ancestors above, descendants below)
   */
  static calculateHourglassLayout(
    root: TreeNode,
    config: TreeLayoutConfig = DEFAULT_TREE_CONFIG
  ): TreeRenderData {
    // For hourglass, we need to build two trees and combine them
    // This is complex and may be implemented in future iterations
    // For now, return standard tree layout
    return this.calculateLayout(root, config, 'tree');
  }

  // ===== Private Helper Methods =====

  /**
   * Convert TreeNode to D3 hierarchy
   */
  private static convertToD3Hierarchy(root: TreeNode): d3.HierarchyNode<TreeNode> {
    const hierarchy = d3.hierarchy(
      root,
      (node: TreeNode) => {
        // For descendant view, return children
        // Respect collapsed state
        if (node.isExpanded === false) {
          return undefined; // Collapsed, don't show children
        }
        return node.children && node.children.length > 0 ? node.children : undefined;
      }
    );

    return hierarchy;
  }

  /**
   * Calculate standard tree layout using D3
   */
  private static calculateTreeLayout(
    root: d3.HierarchyNode<TreeNode>,
    config: TreeLayoutConfig
  ): D3TreeNode {
    const { width, height, nodeWidth, nodeHeight, margin, orientation, siblingSeparation, nonSiblingSeparation } = config;

    // Use nodeSize instead of size to allow tree to grow as needed
    // nodeSize specifies [dx, dy] - the minimum space each node needs
    const horizontalSpacing = nodeWidth * 1.8; // Space between nodes horizontally
    const verticalSpacing = nodeHeight * 2.5; // Space between levels vertically

    // Create tree layout
    const treeLayout = d3.tree<TreeNode>()
      .nodeSize(
        orientation === 'horizontal'
          ? [verticalSpacing, horizontalSpacing]
          : [horizontalSpacing, verticalSpacing]
      )
      .separation((a, b) => {
        // More space between non-siblings
        return a.parent === b.parent ? siblingSeparation : nonSiblingSeparation;
      });

    // Apply layout
    const treeRoot = treeLayout(root) as D3TreeNode;

    // Adjust positions for margins and orientation
    treeRoot.each((node: D3TreeNode) => {
      if (orientation === 'horizontal') {
        // Swap x and y for horizontal layout
        const temp = node.x;
        node.x = node.y + margin.left;
        node.y = temp + margin.top;
      } else {
        node.x = node.x + margin.left;
        node.y = node.y + margin.top;
      }

      // Mark node as visible
      node.isVisible = true;
    });

    return treeRoot;
  }

  /**
   * Calculate cluster layout (equal spacing for all leaves)
   */
  private static calculateClusterLayout(
    root: d3.HierarchyNode<TreeNode>,
    config: TreeLayoutConfig
  ): D3TreeNode {
    const { width, height, margin, orientation } = config;

    const effectiveWidth = width - margin.left - margin.right;
    const effectiveHeight = height - margin.top - margin.bottom;

    const clusterLayout = d3.cluster<TreeNode>()
      .size(
        orientation === 'horizontal'
          ? [effectiveHeight, effectiveWidth]
          : [effectiveWidth, effectiveHeight]
      );

    const clusterRoot = clusterLayout(root) as D3TreeNode;

    // Adjust for margins
    clusterRoot.each((node: D3TreeNode) => {
      if (orientation === 'horizontal') {
        const temp = node.x;
        node.x = node.y + margin.left;
        node.y = temp + margin.top;
      } else {
        node.x = node.x + margin.left;
        node.y = node.y + margin.top;
      }
      node.isVisible = true;
    });

    return clusterRoot;
  }

  /**
   * Calculate radial layout (circular tree)
   */
  private static calculateRadialLayout(
    root: d3.HierarchyNode<TreeNode>,
    config: TreeLayoutConfig
  ): D3TreeNode {
    const { width, height } = config;
    const radius = Math.min(width, height) / 2 - 100;

    const treeLayout = d3.tree<TreeNode>()
      .size([2 * Math.PI, radius])
      .separation((a, b) => (a.parent === b.parent ? 1 : 2) / a.depth);

    const treeRoot = treeLayout(root) as D3TreeNode;

    // Convert polar coordinates to Cartesian
    const centerX = width / 2;
    const centerY = height / 2;

    treeRoot.each((node: D3TreeNode) => {
      const angle = node.x; // x is angle in radial layout
      const radius = node.y; // y is radius

      // Convert to Cartesian coordinates
      node.x = centerX + radius * Math.cos(angle - Math.PI / 2);
      node.y = centerY + radius * Math.sin(angle - Math.PI / 2);
      node.isVisible = true;
    });

    return treeRoot;
  }

  /**
   * Calculate radial layout with custom fan config
   */
  private static calculateRadialLayoutWithConfig(
    root: d3.HierarchyNode<TreeNode>,
    config: TreeLayoutConfig,
    fanConfig: FanChartConfig
  ): D3TreeNode {
    const { width, height } = config;
    const { startAngle, endAngle, innerRadius, outerRadius } = fanConfig;

    const treeLayout = d3.tree<TreeNode>()
      .size([endAngle - startAngle, outerRadius - innerRadius])
      .separation((a, b) => (a.parent === b.parent ? 1 : 2) / a.depth);

    const treeRoot = treeLayout(root) as D3TreeNode;

    const centerX = width / 2;
    const centerY = height / 2;

    treeRoot.each((node: D3TreeNode) => {
      const angle = node.x + startAngle;
      const radius = node.y + innerRadius;

      node.x = centerX + radius * Math.cos(angle);
      node.y = centerY + radius * Math.sin(angle);
      node.isVisible = true;
    });

    return treeRoot;
  }

  /**
   * Extract all nodes from tree, including spouse and sibling nodes positioned adjacent
   */
  private static extractNodes(root: D3TreeNode): D3TreeNode[] {
    // Extract all hierarchy nodes
    const nodes: D3TreeNode[] = [];
    const minSpouseOffset = 200; // Minimum horizontal distance between spouses

    root.each((node: D3TreeNode) => {
      nodes.push(node);
    });

    // Add spouse nodes with simple fixed offset
    // Let D3's layout handle the main spacing, we just position spouses adjacent
    root.each((node: D3TreeNode) => {
      const enhancedSpouses = (node.data as any).enhancedSpouses;

      if (enhancedSpouses && enhancedSpouses.length > 0) {
        enhancedSpouses.forEach((spouseInfo: any, index: number) => {
          // Check if spouse is already in the main tree (to avoid duplicates)
          const existingSpouseNode = nodes.find(
            n => n.data.person.id === spouseInfo.spouse.id
          );

          if (!existingSpouseNode) {
            // Place spouse with fixed offset to the right
            // Use minSpouseOffset (200px) as the spacing
            const spouseNode = {
              data: {
                person: spouseInfo.spouse,
                spouses: [],
                children: [],
                level: node.data.level,
                isExpanded: true,
                _syntheticId: `spouse-${node.data.person.id}-${spouseInfo.spouse.id}`,
                _isSpouseNode: true, // Mark as spouse node for rendering
                _partnerNode: node, // Reference to partner for link drawing
              },
              x: node.x + minSpouseOffset * (index + 1),
              y: node.y,
              depth: node.depth,
              height: node.height,
              parent: node.parent,
              children: [],
              isVisible: true,
              each: () => {},
            } as unknown as D3TreeNode;

            nodes.push(spouseNode);
          } else {
            // Spouse is already in the tree (common in ancestor view where both parents are nodes)
            // Mark both nodes as partners so we can draw a marriage line between them

            // Add partner reference to current node
            if (!(node.data as any)._partners) {
              (node.data as any)._partners = [];
            }
            (node.data as any)._partners.push(existingSpouseNode);

            // Add partner reference to existing spouse node
            if (!(existingSpouseNode.data as any)._partners) {
              (existingSpouseNode.data as any)._partners = [];
            }
            (existingSpouseNode.data as any)._partners.push(node);
          }
        });
      }
    });

    console.log(`TreeLayoutService: Extracted ${nodes.length} nodes (${nodes.filter((n: any) => n.data._isSpouseNode).length} spouses)`);

    return nodes;
  }

  /**
   * Extract all links from tree, creating T-shaped family connectors
   *
   * Family tree connector structure:
   * - Each family unit (parents + children) gets a T-shaped connector
   * - Horizontal line connects the parents (spouse link)
   * - Vertical line drops from midpoint between parents
   * - Children connect to the vertical line with short horizontal branches
   *
   * This matches traditional genealogy chart conventions
   */
  private static extractLinks(root: D3TreeNode, allNodes: D3TreeNode[]): D3TreeLink[] {
    const links: D3TreeLink[] = [];
    const nodeMap = new Map<string, D3TreeNode>();
    const processedFamilies = new Set<string>();
    const spousePairs = new Map<string, D3TreeNode>(); // Map parent to their spouse node

    // Build a map of person ID to D3 node for quick lookup (includes spouse nodes)
    allNodes.forEach((node) => {
      nodeMap.set(node.data.person.id, node);
    });

    // Add spouse links and build spouse pair map
    allNodes.forEach((node) => {
      const isSpouseNode = (node.data as any)._isSpouseNode;
      const partnerNode = (node.data as any)._partnerNode;
      const partners = (node.data as any)._partners;

      // Case 1: Synthetic spouse node (one person is in tree, spouse added as synthetic node)
      if (isSpouseNode && partnerNode) {
        const familyKey = [partnerNode.data.person.id, node.data.person.id].sort().join('-');

        if (!processedFamilies.has(familyKey)) {
          processedFamilies.add(familyKey);

          links.push({
            source: partnerNode,
            target: node,
            type: 'spouse',
            isHighlighted: false,
          });

          // Store spouse pair for parent-child connector logic
          spousePairs.set(partnerNode.data.person.id, node);
          spousePairs.set(node.data.person.id, partnerNode);
        }
      }

      // Case 2: Both partners are in the main tree (common in ancestor view)
      if (partners && partners.length > 0) {
        partners.forEach((partnerNodeRef: D3TreeNode) => {
          const familyKey = [node.data.person.id, partnerNodeRef.data.person.id].sort().join('-');

          if (!processedFamilies.has(familyKey)) {
            processedFamilies.add(familyKey);

            links.push({
              source: node,
              target: partnerNodeRef,
              type: 'spouse',
              isHighlighted: false,
            });

            // Store spouse pair for parent-child connector logic
            spousePairs.set(node.data.person.id, partnerNodeRef);
            spousePairs.set(partnerNodeRef.data.person.id, node);
          }
        });
      }
    });

    // Add parent-child links for all children in hierarchy
    // Store family unit information for T-shaped connectors
    root.each((node: D3TreeNode) => {
      if (node.children && node.children.length > 0) {
        // Check if this parent has a spouse
        const spouseNode = spousePairs.get(node.data.person.id);

        node.children.forEach((child: D3TreeNode) => {
          const biologicalStatus = (child.data as any).biologicalStatus;

          // Use T-shaped connectors for all nodes when both parents exist
          links.push({
            source: node,
            target: child,
            type: 'parent-child',
            isHighlighted: false,
            biologicalStatus: biologicalStatus || 'biological',
            // Add metadata for T-shaped connector rendering
            metadata: {
              spouseNode: spouseNode || undefined,
              siblings: node.children || [],
            },
          });
        });
      }
    });

    console.log(`TreeLayoutService: Created ${links.length} links (${links.filter(l => l.type === 'spouse').length} spouse links)`);

    return links;
  }

  /**
   * Calculate SVG dimensions based on node positions
   */
  private static calculateDimensions(
    nodes: D3TreeNode[],
    config: TreeLayoutConfig
  ): SVGDimensions {
    if (nodes.length === 0) {
      return {
        width: config.width,
        height: config.height,
        viewBox: `0 0 ${config.width} ${config.height}`,
        contentWidth: config.width - config.margin.left - config.margin.right,
        contentHeight: config.height - config.margin.top - config.margin.bottom,
      };
    }

    // Find bounds of all nodes
    let minX = Infinity;
    let maxX = -Infinity;
    let minY = Infinity;
    let maxY = -Infinity;

    nodes.forEach(node => {
      minX = Math.min(minX, node.x);
      maxX = Math.max(maxX, node.x);
      minY = Math.min(minY, node.y);
      maxY = Math.max(maxY, node.y);
    });

    // Add padding for nodes
    const padding = 50;
    const width = maxX - minX + padding * 2;
    const height = maxY - minY + padding * 2;

    return {
      width: Math.max(width, config.width),
      height: Math.max(height, config.height),
      viewBox: `${minX - padding} ${minY - padding} ${width} ${height}`,
      contentWidth: width,
      contentHeight: height,
    };
  }

  /**
   * Calculate node positions for export or external use
   */
  static calculateNodePositions(nodes: D3TreeNode[]): NodePosition[] {
    return nodes.map(node => ({
      personId: node.data.person.id,
      x: node.x,
      y: node.y,
      depth: node.depth,
      isExpanded: node.data.isExpanded ?? true,
    }));
  }

  /**
   * Calculate link paths for rendering
   */
  static calculateLinkPaths(
    links: D3TreeLink[],
    orientation: 'horizontal' | 'vertical' = 'horizontal'
  ): LinkPath[] {
    return links.map(link => {
      const path = orientation === 'horizontal'
        ? this.createHorizontalLinkPath(link)
        : this.createVerticalLinkPath(link);

      return {
        d: path,
        sourceId: link.source.data.person.id,
        targetId: link.target.data.person.id,
        type: link.type,
      };
    });
  }

  /**
   * Create horizontal link path (for horizontal tree)
   */
  private static createHorizontalLinkPath(link: D3TreeLink): string {
    const { source, target } = link;

    // Smooth curve from source to target
    return `M${source.x},${source.y}
            C${(source.x + target.x) / 2},${source.y}
             ${(source.x + target.x) / 2},${target.y}
             ${target.x},${target.y}`;
  }

  /**
   * Create vertical link path (for vertical tree)
   * Handles special cases for spouse links and T-shaped family connectors
   */
  private static createVerticalLinkPath(link: D3TreeLink): string {
    const { source, target, type, metadata } = link;

    // Spouse links: simple horizontal line
    if (type === 'spouse') {
      return `M${source.x},${source.y} L${target.x},${target.y}`;
    }

    // Parent-child links: simple connector from parent (or parent midpoint) to child
    const parent = source;
    const child = target;
    const spouseNode = metadata?.spouseNode;

    // Calculate starting point for connector
    let startX = parent.x;
    let startY = parent.y;

    if (spouseNode) {
      // Start from midpoint between parent and spouse
      startX = (parent.x + spouseNode.x) / 2;
      startY = parent.y;
    }

    // For all children: draw T-shaped connector if both parents exist, otherwise curved line
    if (spouseNode) {
      // Both parents exist: T-shaped connector
      // Vertical line from parent midpoint, horizontal to child, vertical to child
      const barY = startY + (child.y - startY) * 0.5;
      return `M${startX},${startY}
              L${startX},${barY}
              L${child.x},${barY}
              L${child.x},${child.y}`;
    } else {
      // Single parent: simple curved connection
      return `M${source.x},${source.y}
              C${source.x},${(source.y + target.y) / 2}
               ${target.x},${(source.y + target.y) / 2}
               ${target.x},${target.y}`;
    }
  }

  /**
   * Create radial link path (for fan/radial layouts)
   */
  static createRadialLinkPath(link: D3TreeLink, centerX: number, centerY: number): string {
    const { source, target } = link;

    // Calculate angles and radii
    const sourceAngle = Math.atan2(source.y - centerY, source.x - centerX);
    const targetAngle = Math.atan2(target.y - centerY, target.x - centerX);
    const sourceRadius = Math.sqrt(Math.pow(source.x - centerX, 2) + Math.pow(source.y - centerY, 2));
    const targetRadius = Math.sqrt(Math.pow(target.x - centerX, 2) + Math.pow(target.y - centerY, 2));

    // Use arc for curved connection
    return `M${source.x},${source.y}
            Q${centerX + (sourceRadius + targetRadius) / 2 * Math.cos((sourceAngle + targetAngle) / 2)},
             ${centerY + (sourceRadius + targetRadius) / 2 * Math.sin((sourceAngle + targetAngle) / 2)}
             ${target.x},${target.y}`;
  }

  /**
   * Optimize layout for large trees
   * Applies clustering and simplification for trees with many nodes
   */
  static optimizeLayoutForLargeTree(
    renderData: TreeRenderData,
    threshold: number = 500
  ): TreeRenderData {
    if (renderData.visibleNodeCount < threshold) {
      return renderData; // No optimization needed
    }

    // For large trees, we could:
    // 1. Hide distant nodes
    // 2. Cluster similar nodes
    // 3. Simplify link paths
    // 4. Use level-of-detail rendering

    // For now, return as-is
    // Full optimization would be implemented based on performance testing
    return renderData;
  }

  /**
   * Find node by person ID
   */
  static findNodeByPersonId(root: D3TreeNode, personId: string): D3TreeNode | null {
    let foundNode: D3TreeNode | null = null;

    root.each((node: D3TreeNode) => {
      if (node.data.person.id === personId) {
        foundNode = node;
      }
    });

    return foundNode;
  }

  /**
   * Get all ancestors of a node (path to root)
   */
  static getAncestors(node: D3TreeNode): D3TreeNode[] {
    const ancestors: D3TreeNode[] = [];
    let current: D3TreeNode | null = node;

    while (current) {
      ancestors.unshift(current);
      current = current.parent;
    }

    return ancestors;
  }

  /**
   * Get all descendants of a node
   */
  static getDescendants(node: D3TreeNode): D3TreeNode[] {
    const descendants: D3TreeNode[] = [];

    node.each((descendant: D3TreeNode) => {
      descendants.push(descendant);
    });

    return descendants;
  }

  /**
   * Calculate path between two nodes
   */
  static calculatePathBetweenNodes(
    root: D3TreeNode,
    startPersonId: string,
    endPersonId: string
  ): D3TreeNode[] | null {
    const startNode = this.findNodeByPersonId(root, startPersonId);
    const endNode = this.findNodeByPersonId(root, endPersonId);

    if (!startNode || !endNode) {
      return null;
    }

    // Get ancestors of both nodes
    const startAncestors = this.getAncestors(startNode);
    const endAncestors = this.getAncestors(endNode);

    // Find common ancestor
    let commonAncestor: D3TreeNode | null = null;
    for (let i = 0; i < Math.min(startAncestors.length, endAncestors.length); i++) {
      if (startAncestors[i] === endAncestors[i]) {
        commonAncestor = startAncestors[i];
      } else {
        break;
      }
    }

    if (!commonAncestor) {
      return null; // No path exists
    }

    // Build path: start -> common ancestor -> end
    const path: D3TreeNode[] = [];

    // Add path from start to common ancestor
    const startToCommon = startAncestors.slice(startAncestors.indexOf(commonAncestor));
    path.push(...startToCommon.reverse());

    // Add path from common ancestor to end
    const commonToEnd = endAncestors.slice(endAncestors.indexOf(commonAncestor) + 1);
    path.push(...commonToEnd);

    return path;
  }

  // ============================================================================
  // SEARCH AND RELATIONSHIP METHODS (merged from TreeSearchService)
  // ============================================================================

  /**
   * Search for persons in tree by name
   *
   * @param root - Root D3 tree node
   * @param searchTerm - Search query
   * @param limit - Maximum results to return
   * @returns Array of search results sorted by relevance
   */
  static searchInTree(
    root: D3TreeNode,
    searchTerm: string,
    limit: number = 10
  ): TreeSearchResult[] {
    if (!searchTerm || searchTerm.trim().length === 0) {
      return [];
    }

    const term = searchTerm.toLowerCase().trim();
    const results: TreeSearchResult[] = [];

    // Search all nodes
    root.each((node: D3TreeNode) => {
      const person = node.data.person;
      const relevance = this.calculateRelevance(person, term);

      if (relevance > 0) {
        const pathFromRoot = this.getAncestors(node);

        results.push({
          person,
          node,
          pathFromRoot,
          depth: node.depth,
          relevance,
        });
      }
    });

    // Sort by relevance (highest first)
    results.sort((a, b) => b.relevance - a.relevance);

    return results.slice(0, limit);
  }

  /**
   * Calculate relevance score for search term match
   */
  private static calculateRelevance(person: Person, term: string): number {
    let score = 0;

    const firstName = person.firstName?.toLowerCase() || '';
    const lastName = person.lastName?.toLowerCase() || '';
    const middleName = person.middleName?.toLowerCase() || '';
    const maidenName = person.maidenName?.toLowerCase() || '';
    const fullName = `${firstName} ${lastName}`.toLowerCase();

    // Exact matches (highest score)
    if (fullName === term) score += 100;
    if (firstName === term) score += 80;
    if (lastName === term) score += 80;

    // Starts with (high score)
    if (fullName.startsWith(term)) score += 60;
    if (firstName.startsWith(term)) score += 50;
    if (lastName.startsWith(term)) score += 50;

    // Contains (medium score)
    if (fullName.includes(term)) score += 30;
    if (firstName.includes(term)) score += 25;
    if (lastName.includes(term)) score += 25;

    // Middle name and maiden name (lower score)
    if (middleName.includes(term)) score += 15;
    if (maidenName.includes(term)) score += 15;

    return score;
  }

  /**
   * Calculate lineage path between two persons
   *
   * @param root - Root D3 tree node
   * @param startPersonId - Starting person ID
   * @param endPersonId - Ending person ID
   * @returns Lineage path with relationship description, or null if no path exists
   */
  static calculateLineagePath(
    root: D3TreeNode,
    startPersonId: string,
    endPersonId: string
  ): LineagePathResult | null {
    const path = this.calculatePathBetweenNodes(root, startPersonId, endPersonId);

    if (!path || path.length === 0) {
      return null;
    }

    // Calculate relationship description
    const relationship = this.describeRelationship(path);

    // Extract links
    const links = [];
    for (let i = 0; i < path.length - 1; i++) {
      links.push({
        source: path[i],
        target: path[i + 1],
        type: 'parent-child' as const,
        isHighlighted: true,
      });
    }

    return {
      startPersonId,
      endPersonId,
      path,
      links,
      relationshipDescription: relationship.description,
      generationDistance: relationship.generations,
      pathLength: path.length,
    };
  }

  /**
   * Describe relationship between persons in path
   */
  private static describeRelationship(
    path: D3TreeNode[]
  ): { description: string; generations: number } {
    if (path.length === 0) {
      return { description: 'No relationship', generations: 0 };
    }

    if (path.length === 1) {
      return { description: 'Same person', generations: 0 };
    }

    if (path.length === 2) {
      // Direct parent-child or siblings
      const node1 = path[0];
      const node2 = path[1];

      if (node1.parent === node2) {
        return { description: 'Child', generations: 1 };
      }
      if (node2.parent === node1) {
        return { description: 'Parent', generations: 1 };
      }
      if (node1.parent === node2.parent) {
        return { description: 'Sibling', generations: 0 };
      }
    }

    // Multi-generation relationship
    const generations = Math.abs(path[0].depth - path[path.length - 1].depth);

    if (path[0].depth < path[path.length - 1].depth) {
      // Descendant
      if (generations === 2) return { description: 'Grandchild', generations };
      if (generations === 3) return { description: 'Great-grandchild', generations };
      if (generations > 3) {
        const greats = 'Great-'.repeat(generations - 2);
        return { description: `${greats}grandchild`, generations };
      }
    } else if (path[0].depth > path[path.length - 1].depth) {
      // Ancestor
      if (generations === 2) return { description: 'Grandparent', generations };
      if (generations === 3) return { description: 'Great-grandparent', generations };
      if (generations > 3) {
        const greats = 'Great-'.repeat(generations - 2);
        return { description: `${greats}grandparent`, generations };
      }
    }

    // Same generation (cousins, etc.)
    const commonAncestor = this.findCommonAncestor(path);
    if (commonAncestor) {
      const distance = this.calculateCousinDistance(path[0], path[path.length - 1], commonAncestor);
      if (distance.degree === 1) {
        if (distance.removed === 0) return { description: 'First cousin', generations: 0 };
        return { description: `First cousin ${distance.removed} times removed`, generations: distance.removed };
      }
      if (distance.degree === 2) {
        if (distance.removed === 0) return { description: 'Second cousin', generations: 0 };
        return { description: `Second cousin ${distance.removed} times removed`, generations: distance.removed };
      }
      return { description: `${this.ordinal(distance.degree)} cousin ${distance.removed} times removed`, generations: distance.removed };
    }

    return { description: `Relative (${path.length - 1} steps)`, generations };
  }

  /**
   * Find common ancestor in path
   */
  private static findCommonAncestor(path: D3TreeNode[]): D3TreeNode | null {
    if (path.length < 3) return null;

    // The common ancestor is typically in the middle of the path
    // For a path A -> B -> C, B is the common ancestor
    const middleIndex = Math.floor(path.length / 2);
    return path[middleIndex];
  }

  /**
   * Calculate cousin degree and removal
   */
  private static calculateCousinDistance(
    node1: D3TreeNode,
    node2: D3TreeNode,
    commonAncestor: D3TreeNode
  ): { degree: number; removed: number } {
    const depth1 = node1.depth - commonAncestor.depth;
    const depth2 = node2.depth - commonAncestor.depth;

    const minDepth = Math.min(depth1, depth2);
    const maxDepth = Math.max(depth1, depth2);

    return {
      degree: minDepth - 1, // Cousin degree (0 = siblings, 1 = first cousins, etc.)
      removed: maxDepth - minDepth, // Times removed
    };
  }

  /**
   * Convert number to ordinal (1st, 2nd, 3rd, etc.)
   */
  private static ordinal(n: number): string {
    const s = ['th', 'st', 'nd', 'rd'];
    const v = n % 100;
    return n + (s[(v - 20) % 10] || s[v] || s[0]);
  }

  /**
   * Highlight search results (return person IDs to highlight)
   */
  static getHighlightedPersonIds(results: TreeSearchResult[]): string[] {
    return results.map(r => r.person.id);
  }

  /**
   * Get lineage person IDs from path result
   */
  static getLineagePersonIds(lineage: LineagePathResult | null): string[] {
    if (!lineage) return [];
    return lineage.path.map(node => node.data.person.id);
  }

  /**
   * Filter tree nodes by predicate
   */
  static filterNodes(
    root: D3TreeNode,
    predicate: (node: D3TreeNode) => boolean
  ): D3TreeNode[] {
    const filtered: D3TreeNode[] = [];

    root.each((node: D3TreeNode) => {
      if (predicate(node)) {
        filtered.push(node);
      }
    });

    return filtered;
  }

  /**
   * Get statistics about search results
   */
  static getSearchStatistics(results: TreeSearchResult[]): {
    totalResults: number;
    averageDepth: number;
    averageRelevance: number;
    maxDepth: number;
    minDepth: number;
  } {
    if (results.length === 0) {
      return {
        totalResults: 0,
        averageDepth: 0,
        averageRelevance: 0,
        maxDepth: 0,
        minDepth: 0,
      };
    }

    const depths = results.map(r => r.depth);
    const relevances = results.map(r => r.relevance);

    return {
      totalResults: results.length,
      averageDepth: depths.reduce((sum, d) => sum + d, 0) / depths.length,
      averageRelevance: relevances.reduce((sum, r) => sum + r, 0) / relevances.length,
      maxDepth: Math.max(...depths),
      minDepth: Math.min(...depths),
    };
  }
}
